import { Component, ElementRef, HostListener, OnInit, ViewChild } from '@angular/core';
import { NavigationService } from 'src/app/navigation/navigation.service';
import { ModuleInfo } from 'src/shared/models/module-info.model';
import { DashboardService } from '../dashboard.service';
import { ModuleContainerService } from '../module-container/module-container.service';

@Component({
    selector: 'cv-taskbar',
    templateUrl: './taskbar.component.html',
    styleUrls: ['./taskbar.component.scss']
})
export class TaskbarComponent implements OnInit {
    @ViewChild('dropdownMenu', { read: ElementRef }) menu: ElementRef<HTMLDivElement>;

    menuOpened: string = null;
    menuPosition = 0;

    get key() { return this.dashboardService.taskbarKey; }
    get modules() { return this.dashboardService.taskbarModules; }
    get activeDashboard() { return this.dashboardService.activeDashboard; }

    private modulesBeingRemoved = new Set<string>();


    constructor(
        private dashboardService: DashboardService,
        private navigationService: NavigationService,
        private moduleContainerService: ModuleContainerService,
    ) {}

    @HostListener('document: mousedown', ['$event'])
    onMouseDown(event: MouseEvent) {
        // if we don't have a menu open, we can ignore the event
        if (!this.menuOpened) {
            return;
        }

        if (!(event.target instanceof Node) || !this.menu.nativeElement.contains(event.target))
            this.menuOpened = null;
    }

    async ngOnInit() {
        const response = await this.dashboardService.loadTaskbar();
        if (response.status_code !== 200) {
            return;
        }

        this.dashboardService.taskbarKey = response.data.dashboardKey;
        this.dashboardService.taskbarModules = response.data.modules;

        for (const module of this.modules) {
            const eagerLoadedState = response.data.modulesEagerLoad?.[module.moduleKey]?.moduleState;
            let moduleCustomName = eagerLoadedState?.moduleCustomName;
            if (moduleCustomName)
                try { moduleCustomName = JSON.parse(moduleCustomName); } catch { }
            module.CustomName = moduleCustomName ?? module.ModuleName;
        }
    }

    openMenu(moduleKey: string, event: MouseEvent) {
        this.menuOpened = moduleKey;
        this.menuPosition = event.clientX - (this.navigationService.sidebarActive ? 60 : 200);
        event.stopPropagation();
    }

    async deleteModule(moduleKey: string) {
        this.menuOpened = null;

        const module = this.modules.find(m => m.moduleKey === moduleKey);
        if (module == null) {
            return; // module not found
        }

        this.modulesBeingRemoved.add(moduleKey);
        try {
            const response = await this.dashboardService.removeDashboardModule(moduleKey, this.key);
            if (response.status_code < 200 || response.status_code >= 300)
                throw response;

            // re-find module incase index changed
            const moduleIndex = this.modules.findIndex((m) => m.moduleKey === moduleKey);
            if (moduleIndex >= 0)
                this.modules.splice(moduleIndex, 1);
        } finally {
            this.modulesBeingRemoved.delete(moduleKey);
        }
    }

    async restoreModule(index: number, moduleKey: string) {
        const module = this.modules[index];
        if (!module || module.moduleKey !== moduleKey)
            return; // module probably already removed
        if (this.modulesBeingRemoved.has(moduleKey))
            return; // already removing

        this.modulesBeingRemoved.add(moduleKey);

        // auto position module by setting to null
        module.map.position[0] = null;
        module.map.position[1] = null;

        // move module to current dashboard
        try {
            const response = await this.dashboardService.moveDashboardModule(moduleKey, this.key, this.activeDashboard.key);
            if (response.status_code < 200 || response.status_code >= 300)
                throw response;

            const moduleInfo = ModuleInfo.fromModuleInstance(module);
            moduleInfo.moduleKey = moduleKey;
            moduleInfo.fromTaskbar = true;
            this.dashboardService.addModuleEvent.next(moduleInfo);
            this.modules.splice(index, 1);
        } finally {
            this.modulesBeingRemoved.delete(moduleKey);
        }
    }

}
